import { IssuePickerOption } from "./IssuePickerOption"
import {
	Popover,
	PopoverContent,
	PopoverTrigger
} from "~/components/ui/popover"
import { XIcon, LinkIcon } from "lucide-react"
import { toast } from "sonner"
import { Link } from "~/components/Link"
import { DetailsSection } from "~/components/details/DetailsSection"
import { StatusIcon } from "~/components/icons/status"
import { Button } from "~/components/ui/button"
import { IssueSetting } from "./IssueSetting"
import { fancyIssueId } from "~/lib/utils"
import { api } from "~/trpc/react"

export function IssueBlockers({
	compact = false,
	issueId,
	teamId,
	teamSlug,
	orgSlug,
	canEdit
}: {
	compact?: boolean
	issueId: string
	teamId: string
	teamSlug: string
	orgSlug: string
	canEdit: boolean
}) {
	const utils = api.useUtils()
	api.issues.onUpdate.useSubscription(
		{ teamId },
		{
			enabled: !!teamId,
			onData: () => {
				void utils.issues.blockers.invalidate({ teamId, issueId })
				void utils.issues.getTeamIssues.invalidate({ teamId })
			}
		}
	)
	const links = api.issues.blockers.useQuery(
		{ issueId, teamId },
		{ enabled: !!teamId }
	)
	const candidates = api.issues.getTeamIssues.useQuery(
		{ teamId },
		{ enabled: !!teamId && canEdit }
	)
	const setBlocker = api.issues.setBlocker.useMutation({
		onError: (error) => toast.error(error.message),
		onSuccess: async () => {
			await utils.issues.blockers.invalidate()
			await utils.issues.get.invalidate()
			await utils.issues.getTeamIssues.invalidate()
		}
	})
	const content = (
		<DetailsSection title="Blockers">
			<div className="flex flex-col gap-3">
				{links.error ? (
					<p className="text-sm text-text-sub">Unable to load blockers.</p>
				) : links.isLoading ? (
					<p className="text-sm text-text-sub">Loading blockers…</p>
				) : links.data?.blockedBy.length ? (
					links.data.blockedBy.map((blocker) => (
						<div key={blocker.id} className="flex min-w-0 items-center gap-2">
							<StatusIcon status={blocker.status} />
							<Link
								className="min-w-0 flex-1 text-sm hover:underline"
								to={`/${orgSlug}/${teamSlug}/${blocker.friendlyId}`}
							>
								{fancyIssueId(blocker.friendlyId, teamSlug)} · {blocker.title}
							</Link>
							{canEdit && (
								<Button
									variant="ghost"
									size="icon-sm"
									aria-label={`Remove blocker ${fancyIssueId(blocker.friendlyId, teamSlug)}`}
									disabled={setBlocker.isPending}
									onClick={() =>
										setBlocker.mutate({
											teamId,
											issueId,
											blockerId: blocker.id,
											linked: false
										})
									}
								>
									<XIcon className="size-3" />
								</Button>
							)}
						</div>
					))
				) : (
					<p className="text-sm text-text-sub">No blockers</p>
				)}
				{canEdit && (
					<IssueSetting<string | null>
						ariaLabel="Add blocker"
						activeTeamId={teamId}
						popoverSize="xl"
						searchPlaceholder="Search by issue ID or title…"
						customNotFound="No matching issues"
						value={null}
						placeholder="Add blocker"
						showArrow
						showBorder
						withSearch
						disabled={
							setBlocker.isPending ||
							candidates.isLoading ||
							!!candidates.error ||
							!links.data
						}
						options={(candidates.data?.issues ?? [])
							.filter(
								(candidate) =>
									candidate.id !== issueId &&
									!links.data?.blockedBy.some(
										(blocker) => blocker.id === candidate.id
									)
							)
							.map((candidate) => ({
								value: candidate.id,
								searchText: `${fancyIssueId(candidate.friendlyId, teamSlug)} ${candidate.title}`,
								display: (
									<IssuePickerOption
										identifier={fancyIssueId(candidate.friendlyId, teamSlug)}
										title={candidate.title}
										status={candidate.status}
									/>
								)
							}))}
						onChange={(blockerId) => {
							if (blockerId)
								setBlocker.mutate({ teamId, issueId, blockerId, linked: true })
						}}
					/>
				)}
				{!!links.data?.blockedBy.length && (
					<p className="text-xs text-text-sub">
						Unfinished blockers set Blocked. Once all are complete or canceled,
						Blocked returns to To Do.
					</p>
				)}
				{!!links.data?.blocks.length && (
					<div className="flex flex-col gap-2">
						<span className="text-xs font-medium text-text-sub">Blocks</span>
						{links.data.blocks.map((dependent) => (
							<Link
								key={dependent.id}
								className="text-sm hover:underline"
								to={`/${orgSlug}/${teamSlug}/${dependent.friendlyId}`}
							>
								{fancyIssueId(dependent.friendlyId, teamSlug)} ·{" "}
								{dependent.title}
							</Link>
						))}
					</div>
				)}
			</div>
		</DetailsSection>
	)
	if (!compact) return content
	return (
		<Popover>
			<PopoverTrigger asChild>
				<Button
					variant="picker-outline"
					size="sm"
					shape="pill"
					aria-label="Blockers"
				>
					<LinkIcon className="size-4" />
					<span className="max-w-48 truncate">
						{links.data
							? `${links.data.blockedBy.length} blockers`
							: "Blockers"}
					</span>
				</Button>
			</PopoverTrigger>
			<PopoverContent size="lg" layout="panel">
				{content}
			</PopoverContent>
		</Popover>
	)
}
